import { useCallback, useEffect, useState } from 'react';

import analyticsService from '../../../../../services/analyticsService';

type TopicDetailUser = {
  externalId: string;
  extendedSearchCount: number;
  frequency: number;
  lastName: string;
  name: string;
  userId: string;
};

type TopicDetailResponse = {
  endDate: string;
  startDate: string;
  topicId: string;
  users: TopicDetailUser[];
};

type UseTopicDetailProps = {
  courseIds: string[];
  endDate: string | undefined;
  isExpanded: boolean;
  startDate: string | undefined;
  topicId: string | null;
};

type UseTopicDetailReturn = {
  error: string | null;
  loading: boolean;
  users: TopicDetailUser[];
};

function useTopicDetail({ courseIds, endDate, isExpanded, startDate, topicId }: UseTopicDetailProps): UseTopicDetailReturn {
  const [users, setUsers] = useState<TopicDetailUser[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState<boolean>(false);

  const fetchDetail = useCallback(async () => {
    if (!topicId) return;

    setLoading(true);
    setError(null);

    try {
      const data: TopicDetailResponse = await analyticsService.getTopicDetail({
        courseIds,
        startDate,
        endDate,
        topicId,
      });

      setUsers(data.users || []);
      setHasFetched(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [courseIds, startDate, endDate, topicId]);

  useEffect(() => {
    if (isExpanded && !hasFetched) {
      fetchDetail();
    }
  }, [isExpanded, hasFetched, fetchDetail]);

  useEffect(() => {
    setHasFetched(false);
    setUsers([]);
  }, [courseIds, startDate, endDate]);

  return {
    error,
    loading,
    users,
  };
}

export default useTopicDetail;
export type { TopicDetailUser };
